import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Warning24Regular, ShieldCheckmark24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  emailInfo: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
  },
  infoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '60px',
  },
  value: {
    wordBreak: 'break-word',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  campaignBox: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteGreenBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
});

export interface ConfirmDialogProps {
  isOpen: boolean;
  subject: string;
  from: string;
  isCampaign: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  subject,
  from,
  isCampaign,
  onConfirm,
  onCancel,
}) => {
  const styles = useStyles();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onCancel()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Report Phishing Email?</DialogTitle>
          <DialogContent className={styles.content}>
            <div className={styles.emailInfo}>
              <div className={styles.infoRow}>
                <Text className={styles.label}>From:</Text>
                <Text className={styles.value}>{from || '(Unknown sender)'}</Text>
              </div>
              <div className={styles.infoRow}>
                <Text className={styles.label}>Subject:</Text>
                <Text className={styles.value}>{subject || '(No subject)'}</Text>
              </div>
            </div>

            {isCampaign ? (
              <div className={styles.campaignBox}>
                <ShieldCheckmark24Regular />
                <Text>
                  <Badge appearance="filled" color="success">
                    Training Campaign
                  </Badge>{' '}
                  This appears to be a phishing awareness training email.
                </Text>
              </div>
            ) : (
              <div className={styles.warningBox}>
                <Warning24Regular />
                <Text>
                  This email will be reported to the security team for investigation.
                </Text>
              </div>
            )}

            <Text>
              Are you sure you want to report this email as a potential phishing attempt?
            </Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm}>
              Report Phishing
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
